
import React from 'react';

const LearningTransferPage = () => {
  return (
    <div className="App">

      <section className="section" style={{ backgroundColor: 'var(--light)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Learning Transfer</h2>
            <p>How computational thinking skills extend beyond robotics</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '3fr 2fr',
            gap: '3rem',
            alignItems: 'start'
          }}>
            {/* <div>
              <h3 style={{ color: 'var(--primary)', marginBottom: '1.5rem' }}>Understanding Learning Transfer</h3>

              <p style={{ marginBottom: '1rem' }}>
                Learning transfer refers to the ability to apply knowledge and skills learned in one context to new situations.
                This concept is fundamental to education—after all, we don't teach students merely to perform well on specific
                tasks, but rather to develop capabilities they can apply broadly throughout their lives.
              </p>

              <p style={{ marginBottom: '1rem' }}>
                Research distinguishes between different types of transfer:
              </p>

              <ul style={{
                listStyleType: 'none',
                padding: '0',
                margin: '1.5rem 0'
              }}>
                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>1.</span>
                  <strong>Near Transfer:</strong> Applying learning to situations similar to the original learning context.
                  For example, using programming skills learned with one robot to program a different robot.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative',
                  marginBottom: '1rem'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>2.</span>
                  <strong>Far Transfer:</strong> Applying learning to contexts that appear different from the original.
                  For example, using decomposition skills learned in robotics to break down a complex writing assignment.
                </li>

                <li style={{
                  padding: '0.5rem 0 0.5rem 2rem',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: '0',
                    color: 'var(--secondary)',
                    fontWeight: 'bold'
                  }}>3.</span>
                  <strong>Deep vs. Surface Transfer:</strong> Deep transfer involves understanding underlying principles that can be applied broadly,
                  while surface transfer focuses on applying specific techniques without necessarily grasping the foundational concepts.
                </li>
              </ul>

              <h3 style={{ color: 'var(--primary)', marginTop: '2rem', marginBottom: '1.5rem' }}>
                Transfer of Computational Thinking
              </h3>

              <p style={{ marginBottom: '1rem' }}>
                Computational thinking (CT) is particularly valuable because its core components—decomposition, pattern recognition,
                abstraction, and algorithmic thinking—are applicable across virtually all domains and disciplines.
              </p>

              <p style={{ marginBottom: '1rem' }}>
                Research shows that CT skills can transfer to:
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                margin: '1.5rem 0'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  padding: '1.5rem',
                  borderTop: '4px solid var(--secondary)'
                }}>
                  <h4 style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--dark)' }}>Transfer Pathways</h4>

                  <img
                    src="/api/placeholder/400/320"
                    alt="Learning Transfer Model"
                    style={{ width: '100%', height: 'auto', borderRadius: '4px', marginBottom: '1rem' }}
                  />

                  <p style={{
                    textAlign: 'center',
                    fontSize: '0.9rem',
                    color: '#666',
                    fontStyle: 'italic',
                    marginBottom: '1.5rem'
                  }}>
                    How computational thinking skills transfer across domains
                  </p>

                  <div style={{
                    backgroundColor: 'var(--light)',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <h5 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>Research in Progress</h5>
                    <p style={{ fontSize: '0.9rem' }}>
                      We are currently collecting data on how students who use Nozomi Robotics apply computational thinking skills
                      in other academic subjects. Preliminary results show promising transfer effects.
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: 'var(--light)',
                    padding: '1rem',
                    borderRadius: '8px'
                  }}>
                    <h5 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>Assessment Framework</h5>
                    <p style={{ fontSize: '0.9rem' }}>
                      Based on Dr. Shima Salehi's work at Stanford's IDEAL Lab, we've developed assessments that measure not just domain-specific
                      skills but transferable computational thinking abilities.
                    </p>
                  </div>
                </div>

                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  padding: '1.5rem',
                  marginTop: '2rem',
                  borderTop: '4px solid var(--accent)'
                }}>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--dark)' }}>Educator Resources</h4>
                  <p style={{ marginBottom: '1.5rem' }}>
                    Tools to help foster transfer of computational thinking skills:
                  </p>

                  <ul style={{ listStyleType: 'none', padding: '0' }}>
                    <li style={{ marginBottom: '1rem' }}>
                      <Link to="/" style={{
                        display: 'block',
                        padding: '0.8rem',
                        backgroundColor: 'var(--light)',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        color: 'var(--dark)',
                        fontWeight: '500'
                      }}>
                        📊 CT Skills Assessment Guide
                      </Link>
                    </li>
                    <li style={{ marginBottom: '1rem' }}>
                      <Link to="/" style={{
                        display: 'block',
                        padding: '0.8rem',
                        backgroundColor: 'var(--light)',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        color: 'var(--dark)',
                        fontWeight: '500'
                      }}>
                        🔄 Cross-Curricular CT Activities
                      </Link>
                    </li>
                    <li>
                      <Link to="/" style={{
                        display: 'block',
                        padding: '0.8rem',
                        backgroundColor: 'var(--light)',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        color: 'var(--dark)',
                        fontWeight: '500'
                      }}>
                        📝 Reflection Prompts Collection
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </section>

    </div>
  );
};

export default LearningTransferPage;